/** Read-only delivery-methods overview for one notification.
 *
 * Used as the grid's master-detail panel; renders straight from the list-row
 * summary (no extra fetch) — the full editor lives at /notifications/:id.
 */
import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { NotificationListItem } from '@/api/notifications';
import { RENDER_STANDARD, TOUR_OVERRIDE_MODE_LABELS } from '@/api/notifications';

export function NotificationSummaryPanel({ row }: { row: NotificationListItem }) {
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">{row.name}</Typography>
        <Chip size="small" variant="outlined" label={row.event} sx={{ fontFamily: 'monospace' }} />
        <Chip
          size="small"
          variant="outlined"
          label={TOUR_OVERRIDE_MODE_LABELS[row.tour_override_mode] ?? row.tour_override_mode}
        />
        {row.language_policy && (
          <Chip size="small" variant="outlined" label={`policy: ${row.language_policy.codename}`} />
        )}
        {row.disable_default_blocks && (
          <Chip size="small" color="warning" variant="outlined" label="default blocks disabled" />
        )}
      </Stack>
      {row.delivery_methods.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No delivery methods.
        </Typography>
      ) : (
        <Table size="small" sx={{ maxWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Rendering</TableCell>
              <TableCell>Theme</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {row.delivery_methods.map((dm) => (
              <TableRow key={dm.id}>
                <TableCell>{dm.sequence}</TableCell>
                <TableCell>{dm.method_label}</TableCell>
                <TableCell>{dm.enabled ? 'yes' : 'no'}</TableCell>
                <TableCell>
                  {dm.rendering_type == null
                    ? '—'
                    : dm.rendering_type === RENDER_STANDARD
                      ? 'Standard'
                      : 'Custom'}
                </TableCell>
                <TableCell>{dm.theme_codename ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}
